import { ReactNode } from 'react'

import { DisclosureButton } from '@headlessui/react'
import { IconChevronDownSolid, IconChevronRightSolid, IconFile } from '@swaggerhub-portal/component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate, useOutletContext } from 'react-router'

import { calculateNestedMargin } from '../helpers'
import { RootOutletContext } from '~/types/portal'

export const SideNavItem = ({
  path,
  title,
  isActive,
  icon,
  level,
  isExpandable,
  open,
  isApi,
  selectedOperationOrSchema,
  setSelectedOperationOrSchema,
  onClick,
  isMobile,
  isGraphQl = false,
}: {
  path: string,
  title: string,
  isActive?: boolean,
  icon?: ReactNode,
  level: number,
  isExpandable?: boolean,
  open?: boolean,
  isApi?: boolean,
  selectedOperationOrSchema?: string | null,
  setSelectedOperationOrSchema?: (selectedOperationOrSchema: string | null) => void,
  onClick?: (e: any) => void,
  isMobile?: boolean,
  isGraphQl?: boolean,
}) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const navItem =
    <div
      className={clsx(
        'flex w-full break-words overflow-hidden py-[13px] text-body2 items-center',
        (isActive && !selectedOperationOrSchema) ? 'text-consumer-blue font-semibold dark:text-dark-consumer-blue' : 'text-neutral-98 dark:text-neutral-30',
      )}
    >
      <div className={clsx('flex', isGraphQl ? 'items-center h-full' : 'flex-col self-start h-5'
      )}>
        <span className={clsx(
          isActive ? 'text-neutral-70 dark:text-neutral-40' : 'text-neutral-60 dark:text-neutral-40',
          isGraphQl ? '' : 'mt-[0.4rem]'
        )}>
          {icon || <IconFile />}
        </span>
      </div>
      <span className="flex-auto ml-2 overflow-hidden break-words">{title}</span>
    </div>

  const { closeSideNavWithoutTransition } = useOutletContext<RootOutletContext>()

  const navClick = (e) => {
    if (setSelectedOperationOrSchema) { setSelectedOperationOrSchema(null) }
    onClick?.(e)
    closeSideNavWithoutTransition?.()
  }

  return (
    <div
      aria-label={t('consumer.sideNavigation.sideNavItem.ariaLabel')}
      style={{ paddingLeft: calculateNestedMargin(level, isMobile) }}
      className={clsx(level > 0 && 'pr-3')}
    >
      <div className={clsx(
        'flex items-center w-full hover:bg-neutral-10 dark:hover:bg-neutral-95',
        isActive ? 'bg-neutral-10 dark:bg-neutral-95' : 'bg-white dark:bg-neutral-98',
        level > 0 ? 'pl-2 rounded rounded-b-1 rounded-t-1' : 'pl-4',
        (open && isApi && isActive) && '!rounded-b-none'
      )}>
        {isExpandable ?
          (isActive || (!isActive && open)) ?
            <div className="flex w-full">
              <DisclosureButton className="flex items-baseline mt-[0.95rem]">
                <div className="flex w-4 dark:text-neutral-30">
                  {open ? <IconChevronDownSolid width={8} /> : <IconChevronRightSolid width={8} />}
                </div>
              </DisclosureButton>
              <NavLink to={path} className="w-full pr-3" onClick={navClick} >
                {navItem}
              </NavLink>
            </div> :
            <div className="flex w-full">
              <DisclosureButton className="flex items-baseline mt-[0.95rem]">
                <div className="flex w-4 dark:text-neutral-30">
                  <IconChevronRightSolid width={8} />
                </div>
              </DisclosureButton>
              <DisclosureButton
                className="flex items-center w-full pr-3 text-left"
                data-testid="disclosure-nav-button"
                onClick={() => {
                  navigate(path)
                  closeSideNavWithoutTransition?.()
                }}
              >
                {navItem}
              </DisclosureButton>
            </div>
          :
          <div className="flex w-full">
            <NavLink to={path} className="w-full pr-3 ml-4" onClick={navClick}>
              {navItem}
            </NavLink>
          </div>
        }
      </div>
    </div>
  )
}